<template>
  <form
    :action="formAction"
    :method="formMethod === 'get' ? 'get' : 'post'"
    :enctype="multipart ? 'multipart/form-data' : null"
    :id="formId || null"
    @submit.prevent="handleSubmit"
  >
    <input v-if="csrfToken && formMethod !== 'get'" type="hidden" name="authenticity_token" :value="csrfToken">
    <input v-if="effectiveMethod !== 'post' && effectiveMethod !== 'get'" type="hidden" name="_method" :value="effectiveMethod">

    <table class="hv-form-table">
      <tr v-if="showFilenameField">
        <th>Uploaded File Name</th>
        <td><input v-model="filename" :name="`${scopeName}[filename]`" type="text" class="form-control string required"></td>
      </tr>

      <tr v-if="showUploadField">
        <th>Upload File</th>
        <td>
          <input
            :name="`${scopeName}[file]`"
            type="file"
            class="form-control string required"
            @change="onFileChange"
          >
        </td>
      </tr>

      <tr>
        <th>{{ commentLabel }}</th>
        <td><input v-model="comment" :name="`${scopeName}[comment]`" type="text" class="form-control string required"></td>
      </tr>

      <tr v-if="showStorageField">
        <th>{{ storageLabel }}</th>
        <td>
          <select v-model="storageId" :name="`${scopeName}[storage_id]`" class="form-control">
            <option value="">Select storage</option>
            <option v-for="option in storageOptions" :key="`storage-${option.value}`" :value="String(option.value)">
              {{ option.label }}
            </option>
          </select>
        </td>
      </tr>

      <tr v-if="showPublicToggle">
        <th>{{ publicToggleLabel }}</th>
        <td>
          <label class="vm-switch-toggle" :class="{ 'is-disabled': !publicToggleEditable }">
            <input v-model="publicChecked" :name="`${scopeName}[public]`" type="checkbox" :disabled="!publicToggleEditable">
            <span class="vm-switch-slider" aria-hidden="true"></span>
            <span class="vm-switch-copy">
              <strong>{{ publicToggleTitle }}</strong>
              <small v-if="publicToggleEditable">{{ publicToggleHelp }}</small>
              <small v-else>{{ publicToggleDisabledHelp }}</small>
            </span>
          </label>
        </td>
      </tr>

      <tr>
        <th>OS Type</th>
        <td>
          <OsTypeSelect
            :options="osOptions"
            :initial-value="os"
            :input-name="`${scopeName}[os]`"
            empty-label="Select an OS type"
            filter-placeholder="Filter OS types"
            @update:model-value="os = $event"
          />
        </td>
      </tr>
    </table>

    <div class="vm-form-actions">
      <button type="submit" class="btn btn-primary" :disabled="submitDisabled || submitting">{{ submitLabel }}</button>
    </div>
  </form>
</template>

<script setup>
import { computed, ref } from "vue"
import OsTypeSelect from "./OsTypeSelect.vue"

const props = defineProps({
  formAction: {
    type: String,
    required: true
  },
  formMethod: {
    type: String,
    default: "post"
  },
  scopeName: {
    type: String,
    required: true
  },
  osOptions: {
    type: Array,
    default: () => []
  },
  initialOs: {
    type: [String, Number],
    default: ""
  },
  initialFilename: {
    type: String,
    default: ""
  },
  initialComment: {
    type: String,
    default: ""
  },
  initialPublic: {
    type: Boolean,
    default: false
  },
  showPublicToggle: {
    type: Boolean,
    default: true
  },
  publicToggleEditable: {
    type: Boolean,
    default: true
  },
  commentLabel: {
    type: String,
    default: "Name of VM(HDD) Image"
  },
  storageLabel: {
    type: String,
    default: "Storage Pool"
  },
  showStorageField: {
    type: Boolean,
    default: false
  },
  storageOptions: {
    type: Array,
    default: () => []
  },
  initialStorageId: {
    type: [String, Number],
    default: ""
  },
  publicToggleLabel: {
    type: String,
    default: "Register as a Public VM(HDD) Image"
  },
  publicToggleTitle: {
    type: String,
    default: "Public image visibility"
  },
  publicToggleHelp: {
    type: String,
    default: "Allow other users to pick this VM image when creating a VM."
  },
  publicToggleDisabledHelp: {
    type: String,
    default: "Visibility cannot be changed while this image is referenced by a VM."
  },
  showFilenameField: {
    type: Boolean,
    default: false
  },
  showUploadField: {
    type: Boolean,
    default: false
  },
  multipart: {
    type: Boolean,
    default: false
  },
  submitLabel: {
    type: String,
    default: "Save"
  },
  formId: {
    type: String,
    default: ""
  },
  remoteUpload: {
    type: Boolean,
    default: false
  },
  uploadUrl: {
    type: String,
    default: ""
  },
  uploadTitle: {
    type: String,
    default: ""
  }
})

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
const effectiveMethod = props.formMethod.toLowerCase()
const filename = ref(props.initialFilename)
const comment = ref(props.initialComment)
const storageId = ref(String(props.initialStorageId || ""))
const publicChecked = ref(!!props.initialPublic)
const os = ref(String(props.initialOs || ""))
const selectedFile = ref("")
const submitting = ref(false)

function completeSccProgressOverlay(redirectUrl, fallbackUrl, delay, successMessage) {
  window.sccStatusShell?.completeSccProgressOverlay?.(redirectUrl, fallbackUrl, delay, successMessage)
}

function markCloneProgressFailed(message) {
  window.sccStatusShell?.markCloneProgressFailed?.(message)
}

function sccFormatCloneBytes(bytes) {
  return window.sccStatusShell?.sccFormatCloneBytes?.(bytes) || ""
}

function sccSetCloneProgress(percent) {
  window.sccStatusShell?.sccSetCloneProgress?.(percent)
}

function setSccOverlayTexts(options = {}) {
  window.sccStatusShell?.setSccOverlayTexts?.(options)
}

function startInlineUploadOverlay(title, text) {
  window.sccStatusShell?.startInlineUploadOverlay?.(title, text)
}

function onFileChange(event) {
  const file = event?.target?.files?.[0]
  selectedFile.value = file ? file.name : ""
}

const submitDisabled = computed(() => {
  if (!String(os.value || "").trim()) {
    return true
  }

  if (props.showUploadField && !selectedFile.value) {
    return true
  }

  if (props.showStorageField && !String(storageId.value || "").trim()) {
    return true
  }

  return false
})

function nativeSubmit(event) {
  if (event?.target?.submit) {
    event.target.submit()
  }
}

function parseJsonResponse(text) {
  if (!text) {
    return null
  }

  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

async function pollUploadStatus(statusUrl, fallbackRedirectUrl) {
  const poll = async () => {
    const response = await fetch(statusUrl, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })

    const text = await response.text()
    const payload = parseJsonResponse(text)

    if (!response.ok) {
      throw new Error((payload && payload.message) || text || "Failed to read upload progress.")
    }

    if (Number.isFinite(Number(payload.percent))) {
      sccSetCloneProgress(Number(payload.percent))
    }
    if (payload.copied_bytes && payload.total_bytes) {
      setSccOverlayTexts({
        bytes: `${sccFormatCloneBytes(payload.copied_bytes)} / ${sccFormatCloneBytes(payload.total_bytes)}`
      })
    }
    setSccOverlayTexts({
      text: payload.message || "Processing upload..."
    })

    if (payload.status === "completed") {
      completeSccProgressOverlay(payload.redirect_url, fallbackRedirectUrl, 700, payload.message || "Upload completed.")
      return true
    }

    if (payload.status === "failed") {
      throw new Error(payload.error || payload.message || "Upload failed.")
    }

    return false
  }

  const tick = async () => {
    const done = await poll()
    if (!done) {
      window.setTimeout(tick, 1000)
    }
  }

  tick()
}

function handleSubmit(event) {
  if (!props.remoteUpload) {
    nativeSubmit(event)
    return
  }

  const form = event.target
  const uploadUrl = props.uploadUrl || props.formAction
  const uploadTitle = props.uploadTitle || "Uploading file"
  const fileInput = form.querySelector("input[type='file']")
  const fileName = fileInput && fileInput.files && fileInput.files[0] ? fileInput.files[0].name : "file"
  const xhr = new XMLHttpRequest()
  const formData = new FormData(form)

  submitting.value = true

  startInlineUploadOverlay(uploadTitle, `Uploading ${fileName} ...`)

  xhr.open("POST", uploadUrl, true)
  xhr.setRequestHeader("Accept", "application/json")
  xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest")
  if (csrfToken) {
    xhr.setRequestHeader("X-CSRF-Token", csrfToken)
  }

  xhr.upload.addEventListener("progress", function(progressEvent) {
    let percent = 25
    if (progressEvent.lengthComputable && progressEvent.total > 0) {
      percent = Math.round((progressEvent.loaded / progressEvent.total) * 50)
      setSccOverlayTexts({
        bytes: `${sccFormatCloneBytes(progressEvent.loaded)} / ${sccFormatCloneBytes(progressEvent.total)}`
      })
    }

    setSccOverlayTexts({
      text: `Uploading ${fileName} ...`
    })
    sccSetCloneProgress(percent)
  })

  xhr.addEventListener("load", function() {
    const payload = parseJsonResponse(xhr.responseText || "{}") || {}
    submitting.value = false

    if (xhr.status >= 200 && xhr.status < 300) {
      if (payload.status_url) {
        setSccOverlayTexts({
          text: "Upload completed. Processing on server..."
        })
        pollUploadStatus(payload.status_url, form.action).catch((error) => {
          markCloneProgressFailed(error.message || "Upload failed.")
        })
      } else {
        completeSccProgressOverlay(payload.redirect_url, form.action, 700, payload.message || "Upload completed.")
      }
    } else {
      markCloneProgressFailed(payload.message || "Upload failed.")
    }
  })

  xhr.addEventListener("error", function() {
    submitting.value = false
    markCloneProgressFailed("Upload failed.")
  })

  xhr.send(formData)
}
</script>
